import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormArray, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { Subscription } from 'rxjs';
import { NotHighlightPristineErrorStateMatcher } from '@shared/classes/not-highlight-pristine-error-state-matcher';
import { areCostsEqual, distributeCostEvenly, isNullish, roundToPrecision } from '@shared/utils';
import { AbstractFormContainer } from '@shared/classes/abstract-form-container';
import {
  SPECIAL_CARGO_HINT_TOOLTIP,
  SPECIAL_CARGO_UNAVAILABLE_TOOLTIP,
  VOLUMETRIC_WEIGHT_TOOLTIP,
  WEIGHT_INPUT_CONFIG,
  VOLUMETRIC_LENGTH_INPUT_CONFIG,
  VOLUMETRIC_HEIGHT_INPUT_CONFIG,
  VOLUMETRIC_WIDTH_INPUT_CONFIG,
  PLACE_COST_INPUT_CONFIG,
} from './delivery-cargo-places.constants';
import { OptionsSeatControlNames } from '@shared/components/delivery-cargo/components/delivery-cargo-places/delivery-cargo-places.enums';
import {
  calculateVolumetricVolume,
  checkIsCargoSpecial,
  getHalfCost,
  getOptionsSeatMainDimensions,
  placeCostValidators,
  placeNumericValidators,
} from '@shared/components/delivery-cargo/components/delivery-cargo-places/delivery-cargo-places.helpers';
import { OptionsSeatFormValue } from '@shared/components/delivery-cargo/components/delivery-cargo-places/delivery-cargo-places.models';
import { COST_INPUT_CONFIG } from '@shared/components/delivery-cost/delivery-cost.constants';
import { totalCostSumValidator } from '@shared/components/delivery-cost/delivery-cost.helpers';

@Component({
  selector: 'app-delivery-cargo-places',
  templateUrl: './delivery-cargo-places.component.html',
  styleUrls: ['./delivery-cargo-places.component.scss'],
})
export class DeliveryCargoPlacesComponent extends AbstractFormContainer implements OnDestroy, OnInit {
  protected readonly OptionsSeatControlNames = OptionsSeatControlNames;
  protected readonly WEIGHT_INPUT_CONFIG = WEIGHT_INPUT_CONFIG;
  protected readonly VOLUMETRIC_LENGTH_INPUT_CONFIG = VOLUMETRIC_LENGTH_INPUT_CONFIG;
  protected readonly VOLUMETRIC_HEIGHT_INPUT_CONFIG = VOLUMETRIC_HEIGHT_INPUT_CONFIG;
  protected readonly VOLUMETRIC_WIDTH_INPUT_CONFIG = VOLUMETRIC_WIDTH_INPUT_CONFIG;
  protected readonly PLACE_COST_INPUT_CONFIG = PLACE_COST_INPUT_CONFIG;
  protected readonly SPECIAL_CARGO_HINT_TOOLTIP = SPECIAL_CARGO_HINT_TOOLTIP;
  protected readonly VOLUMETRIC_WEIGHT_TOOLTIP = VOLUMETRIC_WEIGHT_TOOLTIP;

  @Input() controlName: string = 'OptionsSeat';

  @Input() isTemplate: boolean = false;

  @Input() invoiceId: string = '';

  matcher = new NotHighlightPristineErrorStateMatcher();

  private totalCostSubscription?: Subscription;

  constructor(protected formBuilder: UntypedFormBuilder) {
    super();
  }

  get OptionsSeat(): UntypedFormArray {
    return this.form.get(this.controlName) as UntypedFormArray;
  }

  get totalCost(): UntypedFormControl {
    return this.form.get('Cost') as UntypedFormControl;
  }

  get isMultiPlace(): boolean {
    return this.OptionsSeat.controls.length > 1;
  }

  static optionsSeatValidations(isTemplate: boolean = false) {
    return {
      [OptionsSeatControlNames.Weight]: ['', placeNumericValidators(isTemplate)],
      [OptionsSeatControlNames.VolumetricLength]: ['', placeNumericValidators(isTemplate)],
      [OptionsSeatControlNames.VolumetricWidth]: ['', placeNumericValidators(isTemplate)],
      [OptionsSeatControlNames.VolumetricHeight]: ['', placeNumericValidators(isTemplate)],
      [OptionsSeatControlNames.VolumetricVolume]: ['', placeNumericValidators(isTemplate)],
      [OptionsSeatControlNames.PackRef]: [''],
      [OptionsSeatControlNames.SpecialCargo]: [false],
      [OptionsSeatControlNames.Cost]: ['', placeCostValidators(isTemplate)],
    };
  }

  ngOnInit(): void {
    super.ngOnInit();
    this.recalculatePlacesCost();
    this.listenTotalCostChange();
  }

  addFormControls(): void {
    const optionsSeatArray: UntypedFormArray = this.formBuilder.array([this.initOptionsSeat()]);
    this.form.addControl(this.controlName, optionsSeatArray);
  }

  initOptionsSeat(): UntypedFormGroup {
    return this.formBuilder.group(DeliveryCargoPlacesComponent.optionsSeatValidations(this.isTemplate));
  }

  addOptionsSeat(): void {
    const OptionsSeatControl = this.OptionsSeat;
    // Going from 1 place to 2 always redistributes: in single-place mode the place's own cost
    // input is hidden (see *ngIf="isMultiPlace" in the template) and isn't a reliable source of
    // truth, so totalCost - the field the user actually filled in - is what must be split.
    // From 2+ places on, the per-place cost inputs are visible and user-editable, so only
    // redistribute if the user hasn't already diverged them.
    const wasSinglePlace = OptionsSeatControl.controls.length === 1;
    const existingCosts = OptionsSeatControl.controls.map(
      (control) => (control.value as OptionsSeatFormValue)[OptionsSeatControlNames.Cost],
    );
    const shouldDistributeCost = wasSinglePlace || areCostsEqual(existingCosts);

    OptionsSeatControl.push(this.initOptionsSeat());

    if (shouldDistributeCost) {
      const distributedCosts = distributeCostEvenly(this.totalCost.value, OptionsSeatControl.length);
      OptionsSeatControl.controls.forEach((control, index) =>
        control.patchValue({ [OptionsSeatControlNames.Cost]: distributedCosts[index] }),
      );
    }

    this.recalculatePlacesCost();
  }

  removeOptionsSeat(index: number): void {
    if (this.isMultiPlace) {
      this.OptionsSeat.removeAt(index);
      this.recalculatePlacesCost();
    }
  }

  copyOptionsSeat(index: number): void {
    const control = this.OptionsSeat;
    if (control.controls.length === 1) {
      // Same as in addOptionsSeat: leaving single-place mode, so the sole place's cost
      // (which was standing in for the whole shipment) gets halved before copying it.
      control.controls[0].patchValue({ [OptionsSeatControlNames.Cost]: getHalfCost(this.totalCost.value) });
    }
    const newSeatControl = this.initOptionsSeat();
    newSeatControl.patchValue(control.controls[index].value);
    control.push(newSeatControl);
    this.recalculatePlacesCost();
  }

  recalculateVolumetricVolume(index: number): void {
    const optionsSeat = this.OptionsSeat.controls[index].value as OptionsSeatFormValue;
    const { length, width, height } = getOptionsSeatMainDimensions(optionsSeat);

    const volumetricVolume = calculateVolumetricVolume(length, width, height);

    this.OptionsSeat.controls[index].patchValue({
      [OptionsSeatControlNames.VolumetricVolume]: volumetricVolume ? String(volumetricVolume) : '',
    });
  }

  recalculatePlacesCost(): void {
    if (!this.totalCost) {
      return;
    }
    this.syncPlaceCostValidators();
    this.syncTotalCost();
  }

  checkSpecialCargoCondition(i: number): boolean {
    const optionsSeat = this.OptionsSeat.controls[i].value as OptionsSeatFormValue;

    const { length, width, height, weight, volumetricVolume } = getOptionsSeatMainDimensions(optionsSeat);

    const isCargoSpecial = checkIsCargoSpecial(weight, volumetricVolume, length, width, height);

    if (!isCargoSpecial) {
      this.OptionsSeat.controls[i].patchValue({ [OptionsSeatControlNames.SpecialCargo]: false }, { emitEvent: false });
    }

    return isCargoSpecial;
  }

  getSpecialCargoTooltip(i: number): string | null {
    return this.checkSpecialCargoCondition(i) ? null : SPECIAL_CARGO_UNAVAILABLE_TOOLTIP;
  }

  ngOnDestroy(): void {
    this.totalCostSubscription?.unsubscribe();
    this.form.removeControl(this.controlName);
    this.totalCost?.enable({ emitEvent: false });
  }

  private syncPlaceCostValidators(): void {
    this.OptionsSeat.controls.forEach((control) => {
      const placeCostControl = control.get(OptionsSeatControlNames.Cost);
      if (!placeCostControl) {
        return;
      }
      placeCostControl.setValidators(
        this.isMultiPlace
          ? [
              placeCostValidators(this.isTemplate),
              totalCostSumValidator(() => this.getPlacesCostTotal(), COST_INPUT_CONFIG.maxValue),
            ]
          : [],
      );
      placeCostControl.updateValueAndValidity({ emitEvent: false });
    });
  }

  private getPlacesCostTotal(): number {
    return this.OptionsSeat.controls.reduce(
      (acc, control) => acc + (Number((control.value as OptionsSeatFormValue)[OptionsSeatControlNames.Cost]) || 0),
      0,
    );
  }

  // totalCost is set with onlySelf so its own valueChanges still fires (delivery-cost.component
  // relies on it to re-check the service cost), without propagating to the whole stepDeliveryForm.
  private syncTotalCost(): void {
    if (this.isMultiPlace) {
      const total = this.getPlacesCostTotal();
      this.totalCost.setValue(total ? roundToPrecision(total, 2) : '', { onlySelf: true });
      this.totalCost.disable({ emitEvent: false });
      return;
    }

    const soleCost = (this.OptionsSeat.controls[0]?.value as OptionsSeatFormValue)?.[OptionsSeatControlNames.Cost];
    if (!isNullish(soleCost)) {
      this.totalCost.setValue(soleCost, { onlySelf: true });
    }
    this.totalCost.enable({ emitEvent: false });
  }

  // Reverse of syncTotalCost: in single-place mode the place's Cost input is hidden and
  // totalCost is the only field the user edits directly (typing it in delivery-cost.component),
  // so mirror it back into OptionsSeat[0].cost - it's still submitted to the backend as-is.
  private listenTotalCostChange(): void {
    this.totalCostSubscription = this.totalCost?.valueChanges.subscribe((value) => {
      if (this.isMultiPlace) {
        return;
      }
      this.OptionsSeat.controls[0]?.patchValue({ [OptionsSeatControlNames.Cost]: value }, { emitEvent: false });
    });
  }
}
